import { Filter } from "lucide-react";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import { ESCALATION_TYPES, STATUS_OPTIONS } from "../../constants/translations";
import { getTypeLabel, getStatusLabel } from "../../utils/helpers";

/**
 * Filters bar for Assigned-Escalations tab.
 *
 * Props:
 *   groups          {array}   — [{ groupId, groupName }]
 *   selectedGroup   {string}
 *   setSelectedGroup {function}
 *   selectedType    {string}
 *   setSelectedType {function}
 *   selectedStatus  {string}
 *   setSelectedStatus {function}
 *   sortOrder       {string}
 *   setSortOrder    {function}
 *   theme           {string}
 *   screenSize      {string}
 *   fonts           {object}
 *   styles          {object}
 *   language        {"en"|"ar"}
 *   t               {object}
 */
const FiltersBar = ({
  groups,
  selectedGroup,
  setSelectedGroup,
  selectedType,
  setSelectedType,
  selectedStatus,
  setSelectedStatus,
  sortOrder,
  setSortOrder,
  theme,
  screenSize,
  fonts,
  styles,
  language,
  t,
}) => {
  const selectStyle = styles.inlineSelect(theme === "dark", language, fonts, screenSize);

  const labelStyle = {
    display: "block",
    marginBottom: "0.5rem",
    fontSize: fonts.small,
    opacity: 0.8,
    color: theme === "dark" ? "rgba(248,250,252,0.9)" : "rgba(30,41,59,0.9)",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: screenSize === "xs" ? "column" : "row",
        gap: "1rem",
        marginBottom: "1.5rem",
        flexWrap: "wrap",
      }}
    >
      {/* Group */}
      <div style={{ flex: 1, minWidth: "200px" }}>
        <label style={labelStyle}>
          <Filter size={14} style={{ marginRight: "0.5rem", verticalAlign: "middle" }} />
          {t.groupLabel}
        </label>
        <ReusableDropdown
          items={[
            { groupId: "all", groupName: t.allGroups },
            ...groups,
          ]}
          value={selectedGroup}
          onChange={(val) => setSelectedGroup(val)}
          placeholder={t.groupLabel}
          theme={theme}
          language={language}
          getItemKey={(item) => item.groupId}
          getItemLabel={(item) => item.groupName}
          showAllOnArrowClick
          showClear={selectedGroup !== "all"}
        />
      </div>

      {/* Type */}
      <div style={{ flex: 1, minWidth: "200px" }}>
        <label style={labelStyle}>{t.escalationType}</label>
        <ReusableDropdown
          items={[
            { id: "all", label: t.allTypes },
            ...ESCALATION_TYPES.map((type) => ({
              id: type,
              label: getTypeLabel(type, language),
            })),
          ]}
          value={selectedType}
          onChange={(val) => setSelectedType(val)}
          placeholder={t.escalationType}
          theme={theme}
          language={language}
          getItemKey={(item) => item.id}
          getItemLabel={(item) => item.label}
          showAllOnArrowClick
          showClear={selectedType !== "all"}
        />
      </div>

      {/* Status */}
      <div style={{ flex: 1, minWidth: "200px" }}>
        <label style={labelStyle}>{t.status}</label>
        <ReusableDropdown
          items={[
            { id: "all", label: t.allStatus },
            ...STATUS_OPTIONS.map((status) => ({
              id: status,
              label: getStatusLabel(status, language),
            })),
          ]}
          value={selectedStatus}
          onChange={(val) => setSelectedStatus(val)}
          placeholder={t.status}
          theme={theme}
          language={language}
          getItemKey={(item) => item.id}
          getItemLabel={(item) => item.label}
          showAllOnArrowClick
          showClear={selectedStatus !== "all"}
        />
      </div>

      {/* Sort */}
      <div style={{ flex: 1, minWidth: "200px" }}>
        <label style={labelStyle}>{t.sortByDate}</label>
        <ReusableDropdown
          items={[
            { id: "desc", label: t.newestFirst },
            { id: "asc", label: t.oldestFirst },
          ]}
          value={sortOrder}
          onChange={(val) => setSortOrder(val)}
          placeholder={t.sortByDate}
          theme={theme}
          language={language}
          getItemKey={(item) => item.id}
          getItemLabel={(item) => item.label}
          showAllOnArrowClick
          showClear={false}
        />
      </div>
    </div>
  );
};

export default FiltersBar;